/**
 * Public Tenant Onboarding page — /onboarding/:token
 *
 * Multi-step wizard:
 *  Step 1 — Company + Admin data + Tenant Address (für AVV-Footer)
 *  Step 2 — Compliance: AVV, DSGVO-Verpflichtung, TOMs
 *           Each doc: scroll-to-bottom → typed name + signature → accept
 *  Step 3 — Final submit (calls sign-compliance + accept)
 */
import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, CheckCircle2, Loader2, AlertTriangle, ShieldCheck,
  FileText, ArrowRight, ArrowLeft,
} from "lucide-react";
import api from "@/lib/api";
import { InlineSignaturePad } from "../components/InlineSignaturePad";

const PARTNER_LABEL = {
  call_center: "Callcenter",
  vertrieb: "Vertrieb",
  installateur: "Installateur",
  full_service: "Full-Service",
  custom: "Individuell",
};

const REQUIRED_SLUGS = ["avv", "dsgvo_verpflichtung", "toms"];

export default function TenantOnboarding() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);  // 1 = data, 2 = compliance, 3 = done
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [legalSlugs, setLegalSlugs] = useState([]);

  // Step 1 — tenant + admin form
  const [form, setForm] = useState({
    name: "", slug: "",
    admin_email: "", admin_phone: "",
    admin_first_name: "", admin_last_name: "",
    tenant_address: "", tenant_representative: "",
    tenant_contact_email: "",
  });
  const [acceptAgb, setAcceptAgb] = useState(false);

  // Step 2 — compliance docs
  const [docs, setDocs] = useState([]);           // raw rendered templates
  const [renderedDocs, setRenderedDocs] = useState({});  // {slug: rendered with tenant ctx}
  const [signed, setSigned] = useState({});       // {slug: {signed_name, signature_png_base64}}
  const [currentDocSlug, setCurrentDocSlug] = useState(null);
  const [scrolledAll, setScrolledAll] = useState({});  // {slug: bool}

  useEffect(() => {
    let cancelled = false;
    api.get(`/tenant-invitations/${token}`)
      .then((r) => {
        if (cancelled) return;
        setInvitation(r.data);
        setForm((f) => ({
          ...f,
          name: r.data.suggested_name || "",
          slug: r.data.suggested_slug || "",
        }));
      })
      .catch((e) => setError(e.response?.data?.detail || "Einladung konnte nicht geladen werden"))
      .finally(() => setLoading(false));
    api.get("/legal/required-acceptances")
      .then((r) => setLegalSlugs(r.data?.required || []))
      .catch(() => {});
  }, [token]);

  // ── Step 1 → 2 transition
  const goToCompliance = async () => {
    const required = ["name", "slug", "admin_email", "admin_phone",
      "admin_first_name", "admin_last_name", "tenant_address",
      "tenant_representative", "tenant_contact_email"];
    for (const f of required) {
      if (!form[f]) { toast.error("Bitte alle Pflichtfelder ausfüllen"); return; }
    }
    if (!acceptAgb) { toast.error("Bitte AGB akzeptieren"); return; }

    setSubmitting(true);
    try {
      const r = await api.get(`/tenant-invitations/${token}/compliance-docs`);
      const docList = r.data?.docs || [];
      setDocs(docList);
      // Render each doc client-side by replacing tenant placeholders.
      const rd = {};
      const ctx = {
        tenant_name: form.name,
        tenant_address: form.tenant_address,
        tenant_representative: form.tenant_representative,
        tenant_contact_email: form.tenant_contact_email,
      };
      for (const d of docList) {
        let body = d.body_md;
        for (const [k, v] of Object.entries(ctx)) {
          body = body.split(`{{${k}}}`).join(v);
        }
        rd[d.slug] = { ...d, body_md_rendered: body };
      }
      setRenderedDocs(rd);
      setCurrentDocSlug(docList[0]?.slug || null);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Compliance-Dokumente konnten nicht geladen werden");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Step 2 — confirm current doc signature
  const allSigned = useMemo(
    () => REQUIRED_SLUGS.every((s) => signed[s]?.signed_name && signed[s]?.signature_png_base64),
    [signed],
  );

  const onScroll = (slug) => (e) => {
    const el = e.target;
    // Allow some tolerance (3px)
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 3) {
      if (!scrolledAll[slug]) setScrolledAll((s) => ({ ...s, [slug]: true }));
    }
  };

  const confirmDocSigned = (slug, signedName, signaturePng) => {
    if (!signedName?.trim() || !signaturePng) return;
    setSigned((s) => ({
      ...s,
      [slug]: { signed_name: signedName.trim(), signature_png_base64: signaturePng },
    }));
    // Auto-advance to next unsigned doc
    const idx = REQUIRED_SLUGS.indexOf(slug);
    for (let i = idx + 1; i < REQUIRED_SLUGS.length; i++) {
      if (!signed[REQUIRED_SLUGS[i]]) {
        setCurrentDocSlug(REQUIRED_SLUGS[i]);
        return;
      }
    }
  };

  // ── Step 2 → 3 final submit
  const finalSubmit = async () => {
    if (!allSigned) { toast.error("Bitte alle Dokumente unterzeichnen"); return; }
    setSubmitting(true);
    try {
      // 1) POST sign-compliance
      const signedDocs = REQUIRED_SLUGS.map((slug) => {
        const d = renderedDocs[slug];
        const s = signed[slug];
        return {
          slug,
          doc_version: d.version,
          doc_hash: _hashDoc(d.body_md_rendered),  // re-computed for server check
          signed_name: s.signed_name,
          signature_png_base64: s.signature_png_base64,
        };
      });
      // Note: doc_hash comes from server-rendered template. Client just echoes
      // what server returned. Server will re-render and compare.
      const signedDocsServerHash = REQUIRED_SLUGS.map((slug) => ({
        slug,
        doc_version: renderedDocs[slug].version,
        doc_hash: renderedDocs[slug].doc_hash,  // server-supplied hash for preview
        signed_name: signed[slug].signed_name,
        signature_png_base64: signed[slug].signature_png_base64,
      }));
      await api.post(`/tenant-invitations/${token}/sign-compliance`, {
        tenant_context: {
          tenant_name: form.name,
          tenant_address: form.tenant_address,
          tenant_representative: form.tenant_representative,
          tenant_contact_email: form.tenant_contact_email,
        },
        signed_docs: signedDocsServerHash,
      });

      // 2) POST accept invitation
      await api.post(`/tenant-invitations/${token}/accept`, {
        name: form.name,
        slug: form.slug,
        admin_email: form.admin_email,
        admin_phone: form.admin_phone,
        admin_first_name: form.admin_first_name,
        admin_last_name: form.admin_last_name,
        legal_acceptance_slugs: legalSlugs,
      });

      setDone(true);
      toast.success("Mandant eingerichtet & Verträge unterschrieben!");
      setTimeout(() => navigate(`/login?phone=${encodeURIComponent(form.admin_phone)}`), 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Einrichtung fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render guards
  if (loading) return <Center><Loader2 className="animate-spin text-zinc-500" size={32} /></Center>;
  if (error) return <Center><AlertTriangle className="text-amber-400 mb-3" size={32} /><p className="text-zinc-300">{error}</p></Center>;
  if (done) return (
    <Center>
      <CheckCircle2 className="text-[#00E575] mb-3" size={48} />
      <h2 className="font-display text-xl font-bold mb-2">Mandant eingerichtet!</h2>
      <p className="text-zinc-300 text-sm font-medium">Alle Verträge unterschrieben. Sie werden zum Login weitergeleitet …</p>
    </Center>
  );
  if (!invitation) return <Center><Loader2 className="animate-spin text-zinc-500" size={32} /></Center>;

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-3" data-testid="tenant-onboarding">
      <div className="w-full max-w-2xl bg-[#18181B] border border-zinc-800 rounded-2xl p-6 my-4">
        <Header invitation={invitation} step={step} />
        {step === 1 && (
          <Step1
            form={form} setForm={setForm}
            acceptAgb={acceptAgb} setAcceptAgb={setAcceptAgb}
            onNext={goToCompliance} submitting={submitting}
          />
        )}
        {step === 2 && (
          <Step2
            docs={docs}
            renderedDocs={renderedDocs}
            signed={signed}
            currentDocSlug={currentDocSlug}
            setCurrentDocSlug={setCurrentDocSlug}
            scrolledAll={scrolledAll}
            onScroll={onScroll}
            confirmDocSigned={confirmDocSigned}
            onBack={() => setStep(1)}
            onSubmit={finalSubmit}
            allSigned={allSigned}
            submitting={submitting}
          />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────── Subcomponents
const Header = ({ invitation, step }) => (
  <>
    <div className="flex items-center gap-2 mb-1">
      <Building2 className="text-amber-400" size={20} />
      <div className="text-[12px] uppercase tracking-widest text-amber-400 font-bold">
        Mandanten-Onboarding · {PARTNER_LABEL[invitation.partner_type] || invitation.partner_type}
      </div>
    </div>
    <h1 className="font-display text-2xl font-bold mb-1">Willkommen bei NOVATUS Sales Pro</h1>
    <div className="flex items-center gap-2 mt-3 mb-5" data-testid="onboarding-stepper">
      <StepDot n={1} cur={step} label="Daten" />
      <div className="flex-1 h-px bg-zinc-800" />
      <StepDot n={2} cur={step} label="Verträge" />
      <div className="flex-1 h-px bg-zinc-800" />
      <StepDot n={3} cur={step} label="Fertig" />
    </div>
  </>
);

const StepDot = ({ n, cur, label }) => {
  const active = n <= cur;
  return (
    <div className="flex items-center gap-1.5">
      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[12px] font-bold
        ${active ? "bg-[#00E575] text-[#09090B]" : "bg-zinc-800 text-zinc-500"}`}>{n}</span>
      <span className={`text-[12px] uppercase tracking-wider ${active ? "text-zinc-200" : "text-zinc-400"}`}>{label}</span>
    </div>
  );
};

const Step1 = ({ form, setForm, acceptAgb, setAcceptAgb, onNext, submitting }) => (
  <>
    <h3 className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold mt-2 mb-2 font-bold">Ihre Firma</h3>
    <div className="space-y-3">
      <Field label="Firmenname *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="tn-name" placeholder="z.B. Polen Callcenter GmbH" />
      <Field label="Slug (URL-fragment) *" value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} testid="tn-slug" placeholder="polen-callcenter" />
      <Field label="Vollständige Geschäftsanschrift *" value={form.tenant_address} onChange={(v) => setForm({ ...form, tenant_address: v })} testid="tn-address" placeholder="Musterstraße 1, 12345 Stadt, Land" />
      <Field label="Vertretungsberechtigte Person *" value={form.tenant_representative} onChange={(v) => setForm({ ...form, tenant_representative: v })} testid="tn-rep" placeholder="z.B. Hans Müller, Geschäftsführer" />
      <Field label="Datenschutz-Kontakt E-Mail *" type="email" value={form.tenant_contact_email} onChange={(v) => setForm({ ...form, tenant_contact_email: v })} testid="tn-contact-email" placeholder="datenschutz@firma.de" />
    </div>

    <h3 className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold mt-5 mb-2 font-bold">Ihr Admin-Account</h3>
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <Field label="Vorname *" value={form.admin_first_name} onChange={(v) => setForm({ ...form, admin_first_name: v })} testid="tn-first" />
        <Field label="Nachname *" value={form.admin_last_name} onChange={(v) => setForm({ ...form, admin_last_name: v })} testid="tn-last" />
      </div>
      <Field label="E-Mail *" type="email" value={form.admin_email} onChange={(v) => setForm({ ...form, admin_email: v })} testid="tn-email" />
      <Field label="Mobil-Telefon * (für Login-OTP)" value={form.admin_phone} onChange={(v) => setForm({ ...form, admin_phone: v })} testid="tn-phone" placeholder="+49 …" />

      <label className="flex items-start gap-2 text-[12px] text-zinc-300 cursor-pointer p-3 bg-[#09090B] rounded-lg border border-zinc-800 font-medium">
        <input type="checkbox" checked={acceptAgb} onChange={(e) => setAcceptAgb(e.target.checked)} data-testid="tn-agb" className="mt-0.5 accent-[#00E575]" />
        <span>
          Ich akzeptiere die <a href="/legal/agb" className="text-[#00E575] underline" target="_blank" rel="noreferrer">AGB</a> und{" "}
          <a href="/legal/privacy" className="text-[#00E575] underline" target="_blank" rel="noreferrer">Datenschutz-Erklärung</a>.
        </span>
      </label>

      <button
        onClick={onNext}
        disabled={submitting}
        data-testid="tn-next-step"
        className="w-full h-11 rounded-xl bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold inline-flex items-center justify-center gap-2 disabled:bg-zinc-800 disabled:text-zinc-500"
      >
        {submitting ? <Loader2 size={14} className="animate-spin" /> : <>Weiter zu den Verträgen <ArrowRight size={14} /></>}
      </button>
    </div>
  </>
);

const Step2 = ({
  docs, renderedDocs, signed, currentDocSlug, setCurrentDocSlug,
  scrolledAll, onScroll, confirmDocSigned, onBack, onSubmit, allSigned, submitting,
}) => {
  const doc = currentDocSlug ? renderedDocs[currentDocSlug] : null;
  if (!doc) return null;

  const isDone = Boolean(signed[doc.slug]);
  const canSign = Boolean(scrolledAll[doc.slug]) || isDone;

  return (
    <div className="space-y-3" data-testid="onboarding-step2">
      {/* Doc-Tabs */}
      <div className="flex gap-1.5 flex-wrap">
        {docs.map((d) => {
          const active = d.slug === currentDocSlug;
          const dn = Boolean(signed[d.slug]);
          return (
            <button
              key={d.slug}
              onClick={() => setCurrentDocSlug(d.slug)}
              data-testid={`tn-tab-${d.slug}`}
              className={`px-3 py-1.5 text-[12px] rounded-lg inline-flex items-center gap-1.5 transition
                ${active ? "bg-[#00E575] text-[#09090B] font-bold"
                          : dn ? "bg-emerald-900/40 text-emerald-300 border border-emerald-800"
                               : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}
            >
              {dn && <CheckCircle2 size={12} />}
              {d.slug === "avv" ? "AVV" : d.slug === "dsgvo_verpflichtung" ? "DSGVO" : "TOMs"}
            </button>
          );
        })}
      </div>

      <div className="bg-[#09090B] border border-zinc-800 rounded-xl p-3">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="text-amber-400" size={14} />
          <h3 className="text-[12px] font-bold text-zinc-100">{doc.title}</h3>
          <span className="text-[9px] text-zinc-300 ml-auto">v{doc.version}</span>
        </div>
        <div
          onScroll={onScroll(doc.slug)}
          data-testid={`tn-doc-body-${doc.slug}`}
          className="prose-novatus text-[11.5px] leading-relaxed text-zinc-300 h-[260px] overflow-y-auto px-1 font-mono whitespace-pre-wrap"
        >
          {doc.body_md_rendered}
        </div>
        {!canSign && (
          <p className="text-[12px] text-amber-400 mt-2">
            ↓ Bitte bis zum Ende scrollen, um zur Unterschrift zu gelangen.
          </p>
        )}
      </div>

      {canSign && (
        <SignBlock
          slug={doc.slug}
          existing={signed[doc.slug]}
          onSigned={(name, png) => confirmDocSigned(doc.slug, name, png)}
        />
      )}

      <div className="flex gap-2 pt-2">
        <button
          onClick={onBack}
          data-testid="tn-back"
          className="px-4 h-11 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 inline-flex items-center gap-1.5 text-[13px]"
        >
          <ArrowLeft size={14} /> Zurück
        </button>
        <button
          onClick={onSubmit}
          disabled={!allSigned || submitting}
          data-testid="tn-finalize"
          className="flex-1 h-11 rounded-xl bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold inline-flex items-center justify-center gap-2 disabled:bg-zinc-800 disabled:text-zinc-500"
        >
          {submitting
            ? <Loader2 size={14} className="animate-spin" />
            : <><ShieldCheck size={14} /> {allSigned ? "Mandant einrichten & Verträge bestätigen" : `Noch ${3 - Object.keys(signed).length} unterschreiben`}</>}
        </button>
      </div>
    </div>
  );
};

const SignBlock = ({ slug, existing, onSigned }) => {
  const [name, setName] = useState(existing?.signed_name || "");
  const [png, setPng] = useState(existing?.signature_png_base64 || null);

  const canConfirm = name.trim().length >= 2 && png;
  const confirm = () => { if (canConfirm) onSigned(name, png); };

  return (
    <div className="bg-[#18181B] border border-zinc-700 rounded-xl p-3 space-y-2.5" data-testid={`tn-sign-block-${slug}`}>
      <div className="text-[12px] uppercase tracking-wider font-bold text-zinc-200">
        Unterzeichnung dieses Dokuments
      </div>
      <Field
        label="Vollständiger Name (Textform-Signatur) *"
        value={name}
        onChange={setName}
        testid={`tn-signed-name-${slug}`}
        placeholder="z.B. Hans Müller"
      />
      <div>
        <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">
          Handschriftliche Unterschrift *
        </label>
        <InlineSignaturePad
          testId={`tn-sig-${slug}`}
          onChange={(d) => setPng(d)}
          height={130}
        />
      </div>
      <button
        onClick={confirm}
        disabled={!canConfirm}
        data-testid={`tn-confirm-sign-${slug}`}
        className="w-full h-10 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-[12px] font-bold inline-flex items-center justify-center gap-1.5 disabled:bg-zinc-800 disabled:text-zinc-300"
      >
        <CheckCircle2 size={14} /> Dieses Dokument verbindlich unterzeichnen
      </button>
      {existing && (
        <p className="text-[12px] text-emerald-400 text-center">✓ bereits unterzeichnet — kann ersetzt werden</p>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", testid, placeholder }) => (
  <div>
    <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">{label}</label>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium"
    />
  </div>
);

const Center = ({ children }) => (
  <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-3">
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center max-w-md w-full">
      {children}
    </div>
  </div>
);

// Tiny stand-in hash for client-side display only; the server re-renders and
// computes the *real* SHA-256, so it's fine that this is a no-op fallback.
function _hashDoc(_body) { return ""; }
